<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Post New Job</h1>
            <p class="text-slate-400">Create a job listing to attract qualified contractors</p>
          </div>
          <router-link to="/company/jobs" class="btn-secondary">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
            Back to Jobs
          </router-link>
        </div>
      </div>

      <!-- Job Creation Form -->
      <div class="max-w-4xl mx-auto">
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
          <form @submit.prevent="createJob" class="space-y-6">
            <!-- Basic Information -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Job Title *</label>
                <input 
                  v-model="jobData.title" 
                  type="text" 
                  class="form-input w-full" 
                  placeholder="e.g., Senior Drilling Engineer"
                  required 
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Job Type *</label>
                <select v-model="jobData.type" class="form-select w-full" required>
                  <option value="">Select Job Type</option>
                  <option value="full-time">Full Time</option>
                  <option value="part-time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="temporary">Temporary</option>
                </select>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Location *</label>
                <input 
                  v-model="jobData.location" 
                  type="text" 
                  class="form-input w-full" 
                  placeholder="e.g., Houston, TX"
                  required 
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Salary Range (Min) *</label>
                <input 
                  v-model="jobData.salary_min" 
                  type="number" 
                  class="form-input w-full" 
                  placeholder="50000"
                  required 
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Salary Range (Max) *</label>
                <input 
                  v-model="jobData.salary_max" 
                  type="number" 
                  class="form-input w-full" 
                  placeholder="80000"
                  required 
                />
              </div>
            </div>

            <!-- Job Description -->
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Job Description *</label>
              <textarea 
                v-model="jobData.description" 
                class="form-input w-full" 
                rows="6" 
                placeholder="Describe the role, responsibilities, and requirements..."
                required 
              ></textarea>
            </div>

            <!-- Requirements -->
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Requirements *</label>
              <textarea 
                v-model="jobData.requirements" 
                class="form-input w-full" 
                rows="4" 
                placeholder="List required skills, experience, certifications..."
                required 
              ></textarea>
            </div>

            <!-- Additional Details -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Experience Level</label>
                <select v-model="jobData.experience_level" class="form-select w-full">
                  <option value="">Select Experience Level</option>
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                  <option value="executive">Executive Level</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Industry</label>
                <select v-model="jobData.industry" class="form-select w-full">
                  <option value="">Select Industry</option>
                  <option value="oil-gas">Oil & Gas</option>
                  <option value="renewable-energy">Renewable Energy</option>
                  <option value="mining">Mining</option>
                  <option value="construction">Construction</option>
                  <option value="manufacturing">Manufacturing</option>
                </select>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Start Date</label>
                <input 
                  v-model="jobData.start_date" 
                  type="date" 
                  class="form-input w-full" 
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Application Deadline</label>
                <input 
                  v-model="jobData.deadline" 
                  type="date" 
                  class="form-input w-full" 
                />
              </div>
            </div>

            <!-- Benefits -->
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Benefits & Perks</label>
              <textarea 
                v-model="jobData.benefits" 
                class="form-input w-full" 
                rows="3" 
                placeholder="Health insurance, 401k, flexible hours, etc..."
              ></textarea>
            </div>

            <!-- Contact Information -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Contact Email</label>
                <input 
                  v-model="jobData.contact_email" 
                  type="email" 
                  class="form-input w-full" 
                  placeholder="hr@company.com"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Contact Phone</label>
                <input 
                  v-model="jobData.contact_phone" 
                  type="tel" 
                  class="form-input w-full" 
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <!-- Job Status -->
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Job Status</label>
              <select v-model="jobData.status" class="form-select w-full">
                <option value="active">Active (Accepting Applications)</option>
                <option value="draft">Draft (Save for Later)</option>
                <option value="paused">Paused (Temporarily Closed)</option>
              </select>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center justify-end space-x-4 pt-6 border-t border-slate-700/50">
              <button 
                type="button" 
                @click="saveDraft" 
                class="btn-secondary"
                :disabled="isSubmitting"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"/>
                </svg>
                Save Draft
              </button>
              
              <button 
                type="submit" 
                class="btn-primary"
                :disabled="isSubmitting"
              >
                <svg v-if="isSubmitting" class="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                <svg v-else class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                </svg>
                {{ isSubmitting ? 'Posting Job...' : 'Post Job' }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Success Modal -->
      <div v-if="showSuccessModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
          <div class="text-center">
            <div class="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-white mb-2">Job Posted Successfully!</h3>
            <p class="text-slate-400 mb-6">Your job listing is now live and accepting applications.</p>
            <div class="flex items-center justify-center space-x-3">
              <button @click="showSuccessModal = false" class="btn-secondary">
                Post Another Job
              </button>
              <router-link to="/company/jobs" class="btn-primary">
                View All Jobs
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'

const userStore = useUserStore()

// Reactive data
const isSubmitting = ref(false)
const showSuccessModal = ref(false)

const jobData = ref({
  title: '',
  type: '',
  location: '',
  salary_min: '',
  salary_max: '',
  description: '',
  requirements: '',
  experience_level: '',
  industry: '',
  start_date: '',
  deadline: '',
  benefits: '',
  contact_email: '',
  contact_phone: '',
  status: 'active'
})

// Methods
const createJob = async () => {
  isSubmitting.value = true
  
  try {
    const response = await fetch('/api/company/create-job', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(jobData.value)
    })
    
    if (response.ok) {
      showSuccessModal.value = true
      // Reset form
      jobData.value = {
        title: '',
        type: '',
        location: '',
        salary_min: '',
        salary_max: '',
        description: '',
        requirements: '',
        experience_level: '',
        industry: '',
        start_date: '',
        deadline: '',
        benefits: '',
        contact_email: '',
        contact_phone: '',
        status: 'active'
      }
    } else {
      const error = await response.json()
      alert('Error creating job: ' + error.message)
    }
  } catch (error) {
    console.error('Error creating job:', error)
    alert('Error creating job. Please try again.')
  } finally {
    isSubmitting.value = false
  }
}

const saveDraft = async () => {
  jobData.value.status = 'draft'
  await createJob()
}

// Lifecycle
onMounted(() => {
  // Set default contact info from company profile
  if (userStore.user?.email) {
    jobData.value.contact_email = userStore.user.email
  }
})
</script>

<style scoped>
.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}
</style>
